/**
 * Evaluates an arithmetic expression supplied as tokens — numeric literals, the
 * four operators and parentheses — and returns null when they do not form a
 * well-formed expression.
 *
 * `toNumber` decides what counts as a numeric literal and converts it, so a
 * caller that may only see plain constants can reject everything else while one
 * reading values substituted into a formula can accept the wider form.
 *
 * `isBlank` names the token that stands for a blank input. It reads as 0, but a
 * divisor made only of blank inputs gives a quotient of 0: nothing has been
 * entered yet. A division by any other zero makes the whole result NaN, whatever
 * follows it, so a caller can tell it apart from a malformed expression.
 *
 * Both halves of the formula feature — the builder's constant folding and the
 * runtime calculation — evaluate through here, so they cannot disagree about
 * what an expression means. Recursive descent rather than `eval`, because an
 * expression reaching this point came from stored data.
 */
export function evaluateArithmeticTokens(
  tokens: string[],
  toNumber: (token: string) => number | null,
  isBlank: (token: string) => boolean = () => false
): number | null {
  // A value, and whether only blank inputs made it. A sum is blank when both
  // sides are, and a product when either side is, since a blank factor alone
  // makes it 0.
  type Operand = { value: number; isBlank: boolean };

  let pos = 0;
  let hasDividedByZero = false;

  const divide = (dividend: Operand, divisor: Operand): Operand => {
    if (divisor.value !== 0) {
      return {
        value: dividend.value / divisor.value,
        isBlank: dividend.isBlank,
      };
    }

    if (!divisor.isBlank) {
      hasDividedByZero = true;
    }

    return { value: 0, isBlank: divisor.isBlank };
  };

  // Addition and subtraction bind the loosest, so they are read at the
  // outermost level: one term, then any number of terms added or subtracted
  // after it.
  const parseExpression = (): Operand | null => {
    let left = parseTerm();
    if (left === null) return null;

    while (tokens[pos] === '+' || tokens[pos] === '-') {
      const operator = tokens[pos++];
      const right = parseTerm();
      if (right === null) return null;
      left = {
        value:
          operator === '+'
            ? left.value + right.value
            : left.value - right.value,
        isBlank: left.isBlank && right.isBlank,
      };
    }

    return left;
  };

  // Multiplication and division bind tighter, so they are read one level in:
  // reading them here is what makes "2 + 3 * 4" evaluate to 14 rather than 20.
  const parseTerm = (): Operand | null => {
    let left = parseFactor();
    if (left === null) return null;

    while (tokens[pos] === '*' || tokens[pos] === '/') {
      const operator = tokens[pos++];
      const right = parseFactor();
      if (right === null) return null;
      left =
        operator === '*'
          ? {
              value: left.value * right.value,
              isBlank: left.isBlank || right.isBlank,
            }
          : divide(left, right);
    }

    return left;
  };

  // The smallest piece an operator can apply to: a numeric literal, a blank, a
  // parenthesised expression, or any of those behind a leading sign.
  const parseFactor = (): Operand | null => {
    const token = tokens[pos];

    if (token === '-' || token === '+') {
      pos++;
      const operand = parseFactor();
      if (operand === null) return null;
      return token === '-'
        ? { value: -operand.value, isBlank: operand.isBlank }
        : operand;
    }

    if (token === '(') {
      pos++;
      const operand = parseExpression();
      if (operand === null || tokens[pos] !== ')') return null;
      pos++;
      return operand;
    }

    if (token === undefined) return null;

    if (isBlank(token)) {
      pos++;
      return { value: 0, isBlank: true };
    }

    const value = toNumber(token);
    if (value === null) return null;

    pos++;
    return { value, isBlank: false };
  };

  const result = parseExpression();

  // A well-formed expression consumes every token.
  if (result === null || pos !== tokens.length) {
    return null;
  }

  return hasDividedByZero ? NaN : result.value;
}
